// Stateless SSE chunk parser. Takes whatever bytes the ReadableStream
// surfaced and returns parsed events plus any trailing partial event the
// caller should prepend to the NEXT chunk.
//
// SSE format: each event is `data: <json>\n\n`. Blank lines separate
// events; lines starting with `:` are comments (keepalives).

export type SSEEvent = Record<string, unknown>;

export type SSEParseResult = {
  events: SSEEvent[];
  remainder: string;
};

export function parseSSEChunk(chunk: string): SSEParseResult {
  const events: SSEEvent[] = [];
  // Normalize CRLF → LF so the splitter works uniformly.
  const normalized = chunk.replace(/\r\n/g, '\n');

  // Events are separated by blank lines.
  const parts = normalized.split('\n\n');
  // Last part may be incomplete (no trailing blank line yet).
  const remainder = parts.pop() ?? '';

  for (const block of parts) {
    if (!block.trim()) continue;
    // An event can have multiple `data:` lines that concatenate. SSE spec
    // says join with newlines; we use plain concat since our payloads are
    // single-line JSON anyway.
    let dataParts = '';
    for (const line of block.split('\n')) {
      if (line.startsWith(':')) continue; // comment / keepalive
      if (line.startsWith('data:')) {
        dataParts += line.slice(5).trimStart();
      }
    }
    if (!dataParts) continue;
    try {
      const parsed = JSON.parse(dataParts);
      if (parsed && typeof parsed === 'object') events.push(parsed as SSEEvent);
    } catch {
      // Malformed event — silently drop, never crash the stream.
    }
  }

  return { events, remainder };
}
